import styled from "styled-components";
import { Section, SectionHeading, SectionSubtitle, AnimatedCard } from "./Common";
import chromeIcon from "../assets/chrome.png";
import safariIcon from "../assets/safari.png";
import edgeIcon from "../assets/Microsoft_Edge_logo_(2019).svg.png";
import firefoxIcon from "../assets/firefox.png";
import appStoreIcon from "../assets/apple app store.png";
import googlePlayIcon from "../assets/google play store.png";

const iconImage = (src) => (
  <img src={src} alt="" width={48} height={48} style={{ display: "block" }} />
);

const storeIconImage = (src, variant) => (
  <StoreIcon data-variant={variant}>
    <img src={src} alt="" />
  </StoreIcon>
);

// Normalizes the store badges to the same footprint for consistent sizing
const StoreIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ "data-variant": variant }) => (variant === "google" ? "205px" : "180px")};
  height: ${({ "data-variant": variant }) => (variant === "google" ? "62px" : "54px")};

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

const Card = styled(AnimatedCard)`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 24px;
  display: grid;
  gap: 16px;
  box-shadow: 0 14px 28px rgba(14, 22, 36, 0.1);
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h3};
  text-align: center;
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.muted};
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
  grid-template-columns: 1fr;

  &[data-layout="compact"] {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  font-weight: 600;
  width: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, filter 0.2s ease;
  cursor: pointer;
  text-decoration: none;

  span[aria-hidden="true"] {
    display: inline-flex;
    justify-content: center;
    width: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(14, 22, 36, 0.16);
    border-color: ${({ theme }) => theme.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  &[data-status="soon"] {
    opacity: 0.55;
    border-color: ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.muted};
    cursor: default;
    pointer-events: none;
    box-shadow: none;
    filter: grayscale(100%);
  }

  &[data-status="soon"] span {
    color: ${({ theme }) => theme.muted};
  }
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  background: ${({ theme, $active }) => ($active ? theme.accent : theme.border)};
  color: ${({ theme, $active }) => ($active ? theme.accentContrast : theme.muted)};
`;

const ButtonText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
`;

const desktopDownloads = [
  {
    label: "Chrome",
    icon: iconImage(chromeIcon),
    href: "https://chromewebstore.google.com",
    status: "soon"
  },
  {
    label: "Safari",
    icon: iconImage(safariIcon),
    href: "https://developer.apple.com/safari/extensions",
    status: "available"
  },
  {
    label: "Edge",
    icon: iconImage(edgeIcon),
    href: "https://microsoftedge.microsoft.com/addons",
    status: "soon"
  },
  {
    label: "Firefox",
    icon: iconImage(firefoxIcon),
    href: "https://addons.mozilla.org",
    status: "soon"
  }
];

const mobileDownloads = [
  {
    label: "App Store",
    icon: storeIconImage(appStoreIcon, "apple"),
    href: "https://apps.apple.com",
    status: "available"
  },
  {
    label: "Google Play",
    icon: storeIconImage(googlePlayIcon, "google"),
    href: "https://play.google.com",
    status: "soon"
  }
];

function Downloads() {
  return (
    <Section id="downloads">
      <SectionHeading>Download Texmu</SectionHeading>
      <SectionSubtitle>Choose your platform to start browsing without the noise.</SectionSubtitle>
      <Grid>
        <Card style={{ animationDelay: "0.2s" }}>
          <Title>Desktop browsers</Title>
          <Description>
            Texmu runs locally in Chromium-based browsers with a focus on speed and privacy.
          </Description>
          <ButtonGroup data-layout="compact">
            {desktopDownloads.map((item) => {
              const isAvailable = item.status === "available";
              const elementProps = isAvailable
                ? { as: "a", href: item.href, target: "_blank", rel: "noopener noreferrer" }
                : { type: "button" };

              return (
                <DownloadButton
                  key={item.label}
                  {...elementProps}
                  data-status={isAvailable ? "available" : "soon"}
                  aria-disabled={isAvailable ? undefined : true}
                  tabIndex={isAvailable ? undefined : -1}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <ButtonText>
                    <span>{item.label}</span>
                    <StatusBadge $active={isAvailable}>
                      {isAvailable ? "Available" : "Coming soon"}
                    </StatusBadge>
                  </ButtonText>
                </DownloadButton>
              );
            })}
          </ButtonGroup>
        </Card>
        <Card style={{ animationDelay: "0.28s" }}>
          <Title>Mobile</Title>
          <Description>
            Take your filters on the go with native badges for iPhone, iPad, and Android.
          </Description>
          <ButtonGroup data-layout="compact">
            {mobileDownloads.map((item) => {
              const isAvailable = item.status === "available";
              const elementProps = isAvailable
                ? { as: "a", href: item.href, target: "_blank", rel: "noopener noreferrer" }
                : { type: "button" };

              return (
                <DownloadButton
                  key={item.label}
                  {...elementProps}
                  data-status={isAvailable ? "available" : "soon"}
                  aria-disabled={isAvailable ? undefined : true}
                  tabIndex={isAvailable ? undefined : -1}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <ButtonText>
                    <span>{item.label}</span>
                    <StatusBadge $active={isAvailable}>
                      {isAvailable ? "Available" : "Coming soon"}
                    </StatusBadge>
                  </ButtonText>
                </DownloadButton>
              );
            })}
          </ButtonGroup>
        </Card>
      </Grid>
    </Section>
  );
}

export default Downloads;
